/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Dropdown,
  List,
  Menu,
  message,
  Popconfirm,
  Table,
  Input,
  Space,
  Tooltip,
} from 'antd';

import {
  DownloadOutlined,
  DownOutlined,
  LinkOutlined,
  DeleteOutlined,
  PlusOutlined,
  SyncOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as axios from 'axios';
import moment from 'moment';
import CustomContent from '../../../components/Content/CustomContent';
import {
  getBeaconsAction,
  saveBeaconAction,
} from '../../../actions/AppCms/BeaconActions';

import { history } from '../../../utils/history';
import { host } from '../../../utils/config';
import Authorized from '../../../components/Authorization/Authorized';
import UserContext from '../../layout/UserContext';
import checkPermission from '../../../components/Authorization/Util';
import checkEnabledFeatures from '../../../components/Authorization/Feature';
import { syncBeaconsService } from '../../../services/appCmsServices/BeaconService';
import AddBeaconModal from './AddBeaconModal';
import { showSyncResult, syncErrorReason } from './syncMessages';

const BeaconList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [selectedItem, setSelectedItem] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const currentUser = useContext(UserContext);
  const kontaktSyncEnabled = checkEnabledFeatures(currentUser, 'Kontakt-Sync');
  let canAddBeacon = false;
  try {
    canAddBeacon = !!checkPermission(currentUser, 'appcms', 'write');
  } catch (e) {
    canAddBeacon = false;
  }

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await dispatch(syncBeaconsService());
      showSyncResult(response.data);
      dispatch(getBeaconsAction(beaconFilterParams));
    } catch (error) {
      message.error(
        'Sync failed: ' +
          syncErrorReason(error) +
          (kontaktSyncEnabled ? '. It has been recorded in Sync Logs.' : ''),
        10,
      );
    } finally {
      setSyncing(false);
    }
  };

  const { beacons, beaconFilterParams } = useSelector(store => ({
    beacons: store.beaconReducer.beacons,
    beaconFilterParams: store.filterReducer.beaconFilterParams,
  }));

  useEffect(() => {
    dispatch(getBeaconsAction(beaconFilterParams));
  }, [beaconFilterParams]);

  const handleSingleUnassign = () => {
    const values = {
      uniqueId: selectedItem.uniqueId,
      mac: selectedItem.mac,
      pointOfInterestId: null,
      groupId: null,
    };


    dispatch(saveBeaconAction(values)).then(() => {
      message.success(t('appCms.beacon.beaconUnassigned'));
    });
  };

  const handleMenuClick = action => {
    if (action.key === 'assign' && !selectedItem.conflict) {
      const updatePath =
        '/assignBeacon/' + selectedItem.uniqueId + '/' + selectedItem.id;

      history.push(updatePath);
    }
  };

  const handleDownload = action => {
    const bearer = localStorage.getItem('userToken');

    let options = {
      url: host + '/api/beacons/export',
      method: 'GET',
      responseType: 'blob', // important
      headers: { Authorization: bearer },
    };
    if (action.key === '1') {
      options = {
        ...options,
        params: {
          all: true,
        },
      };
    }
    axios(options).then(response => {

      const today = new Date();
      const currentDate =
        today.getFullYear() +
        '-' +
        (today.getMonth() + 1) +
        '-' +
        today.getDate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'beacon-reports-' + currentDate + '.csv');
      document.body.appendChild(link);
      link.click();
    });
  };

  const unassign =
    selectedItem &&
      ((selectedItem.poiList && selectedItem.poiList.length > 0) ||
        selectedItem.pointOfInterestGroup !== null) ? (
      <Menu.Item key="unassign">
        <Popconfirm
          title={t('appCms.beacon.sureToUnassign')}
          placement="left"
          icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
          onConfirm={() => handleSingleUnassign()}
        >
          <DeleteOutlined />
          {t('appCms.beacon.unassignBeacon')}
        </Popconfirm>
      </Menu.Item>
    ) : (
      ''
    );

  const menu = (
    <Authorized asset="appcms" operation="write">
      <Menu onClick={handleMenuClick}>
        <Menu.Item key="assign" disabled={!!(selectedItem && selectedItem.conflict)}>
          <LinkOutlined />
          {t('appCms.beacon.assignBeacon')}
        </Menu.Item>
        {unassign}
      </Menu>
    </Authorized>
  );

  const exportMenu = (
    <Menu onClick={handleDownload}>
      <Menu.Item key="1">
        <DownloadOutlined />
        {' '}{t('appCms.beacon.allBeacons')}
      </Menu.Item>
    </Menu>
  );

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = clearFilters => {
    clearFilters();
  };

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            {t('appCms.beacon.search')}
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            {t('appCms.beacon.reset')}
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : '',
  });

  const columns = [
    {
      title: t('appCms.beacon.uniqueId'),
      dataIndex: ['uniqueId'],
      key: 'uniqueId',
      ...getColumnSearchProps('uniqueId'),
      render: (uniqueId, record) =>
        record.conflict ? (
          <Tooltip
            title={
              'Major ' +
              record.major +
              ' / minor ' +
              record.minor +
              ' is also used by ' +
              record.conflictsWith +
              ". This beacon can't be assigned until its major/minor is changed in Kontakt.io."
            }
          >
            <span style={{ color: '#f5222d', fontWeight: 500 }}>
              <WarningOutlined /> {uniqueId}
            </span>
          </Tooltip>
        ) : (
          uniqueId
        ),
    },
    {
      title: t('appCms.beacon.macAddress'),
      dataIndex: ['mac'],
      key: 'mac',
      ...getColumnSearchProps('mac'),
    },
    {
      title: t('appCms.beacon.major'),
      dataIndex: ['major'],
      key: 'major',
      ...getColumnSearchProps('major'),
    },
    {
      title: t('appCms.beacon.minor'),
      dataIndex: ['minor'],
      key: 'minor',
      ...getColumnSearchProps('minor'),
    },
    {
      title: t('appCms.beacon.batteryLevel'),
      dataIndex: ['batteryLevel'],
      key: 'batteryLevel',
    },
    {
      title: t('appCms.beacon.txPower'),
      dataIndex: ['txPower'],
      key: 'txPower',
    },
    {
      title: t('appCms.beacon.lastSyncedTimestamp'),
      dataIndex: ['lastSyncedTimestamp'],
      key: 'lastSyncedTimestamp',
      render: lastSyncedTimestamp => (
        <span>
          {lastSyncedTimestamp
            ? moment.unix(lastSyncedTimestamp).format('YYYY-MM-DD HH:mm:ss')
            : ''}
        </span>
      ),
    },
    {
      title: t('appCms.beacon.assignedPoiGroup'),
      dataIndex: ['pointOfInterestGroup'],
      key: 'pointOfInterestGroup',
      render: text =>
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        text ? (
          <a
            aria-hidden="true"
            onClick={() => {
              history.push('/updatePoiGroup/' + text.id);
            }}
          >
            <span>{text.title}</span>
          </a>
        ) : (
          ''
        ),
    },
    {
      title: t('appCms.beacon.assignedPoi'),
      dataIndex: ['poiList'],
      key: 'poiList',
      render: (text, record) =>
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
        record.pointOfInterestGroup == null && text.length > 0 ? (
          <a
            aria-hidden="true"
            onClick={() => {
              history.push('/updatePoi/' + text[0].id);
            }}
          >
            <span>{text[0].title}</span>
          </a>
        ) : (
          ''
        ),
    },

    {
      title: t('common.actions'),
      key: 'action',
      render: () => (
        <span>
          <Dropdown overlay={menu} trigger={['click']}>
            <a className="ant-dropdown-link" href="#">
              {t('common.actions')} <DownOutlined />
            </a>
          </Dropdown>
        </span>
      ),
    },
  ];

  return (
    <CustomContent>
      <Dropdown overlay={exportMenu}>
        <Button
          icon={<DownloadOutlined />}
          style={{ float: 'right', marginBottom: 16, marginRight: 16 }}
        >
          {t('appCms.export')}
        </Button>
      </Dropdown>
      <Popconfirm
        title={
          kontaktSyncEnabled
            ? 'Sync all beacons from Kontakt.io now?'
            : 'Add every Kontakt.io beacon that is missing from the database?'
        }
        placement="bottomRight"
        disabled={!canAddBeacon}
        onConfirm={handleSync}
      >
        <Button
          icon={<SyncOutlined />}
          loading={syncing}
          disabled={!canAddBeacon}
          style={{ float: 'right', marginBottom: 16, marginRight: 16 }}
        >
          Sync
        </Button>
      </Popconfirm>
      <Tooltip title={canAddBeacon ? '' : "You don't have permission to add beacons"}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={!canAddBeacon}
          onClick={() => setAddModalVisible(true)}
          style={{ float: 'right', marginBottom: 16, marginRight: 16 }}
        >
          Add Beacon
        </Button>
      </Tooltip>
      <AddBeaconModal
        visible={addModalVisible}
        onClose={() => setAddModalVisible(false)}
        onSuccess={() => {
          setAddModalVisible(false);
          dispatch(getBeaconsAction(beaconFilterParams));
        }}
      />
      <Divider />
      <Table
        columns={
          kontaktSyncEnabled
            ? columns
            : columns.filter(column => column.key !== 'major' && column.key !== 'minor')
        }
        dataSource={beacons}
        expandable={{
          expandedRowRender: record => (
            <List
              size="small"
              header={
                <strong>
                  {t('appCms.beacon.assignedPois')}{' '}
                  {record.pointOfInterestGroup
                    ? ' (' + record.pointOfInterestGroup.title + ')'
                    : ''}
                </strong>
              }
              bordered
              dataSource={record.poiList}
              renderItem={item => (
                <List.Item key={item.id}>{item.title}</List.Item>
              )}
            />
          ),
          rowExpandable: record => record.pointOfInterestGroup !== null,
        }}
        pagination={{
          defaultPageSize: beaconFilterParams.size,
          total: beacons.totalElements,
          showTotal: (total, range) => {
            return t('common.paginationTotal', { start: range[0], end: range[1], total });
          },
          showSizeChanger: true,
          pageSizeOptions: [
            '10',
            '20',
            '30',
            '40',
            '50',
            '100',
            '200',
            '500',
            '1000',
          ],
        }}
        // onChange={handleTableChange}
        rowKey={record => record.uniqueId}
        onRow={record => {
          return {
            onClick: () => setSelectedItem(record),
            style: record.conflict ? { background: '#fff1f0' } : undefined,
          };
        }}
      />
    </CustomContent>
  );
};

export default BeaconList;
